import { postAction } from './client';
import { currentSession, signOut } from '../auth/session';
import { bustCache } from '../lib/swr';
import type { PublicConfig, FieldDataType } from './types';

type Row = Record<string, unknown>;

export interface TemplateFieldAdmin {
  sectionKey: string;
  sectionLabel: string;
  fieldKey: string;
  label: string;
  unit: string;
  dataType: FieldDataType;
  required: boolean;
  sanityMin: number | null;
  sanityMax: number | null;
  /** dataType 'select' only — the fixed list of choices (min 2). */
  options?: string[];
  col?: string; // READINGS sheet column (returned by adminListTemplates)
}
export interface TemplateAdmin {
  templateId: string;
  title: string;
  version: string;
  builtin?: boolean;
  usedBy?: string[];
  fields: TemplateFieldAdmin[];
}

async function call<T>(action: string, payload: Record<string, unknown> = {}): Promise<T> {
  const ses = currentSession();
  if (!ses) throw new Error('Not signed in.');
  const res = await postAction<T & { ok: boolean; error?: string; message?: string }>(action, { ...payload, token: ses.token });
  if (!res.ok) {
    if (res.error === 'AUTH_REQUIRED' || res.error === 'AUTH_INVALID') signOut();
    throw new Error(res.message || res.error || 'Request failed');
  }
  // any successful write invalidates the SWR read caches that could now be stale
  if (/^admin(Save|Delete|ImportReadings|PatchReadings|SetPmsDays)/.test(action)) bustCache();
  return res;
}

export interface DashboardData {
  todayPmsCycleDay?: number;
  totals: { equipment: number; readingsToday: number; due: number; overdue: number; abnormalToday: number };
  equipment: Array<{
    equipmentId: string; name: string; location: string; category?: 'daily' | 'pms';
    pmsCycleDay?: number | null; pmsGroup?: string;
    lastReadingAt: string | null; nextRecommendedAt: string | null; status: string;
  }>;
}

export const adminApi = {
  dashboard: () => call<{ ok: true } & DashboardData>('adminDashboard'),

  /** One call for the Trends screen: equipment + templates + thresholds + config. */
  trends: () => call<{
    ok: true;
    equipment: Row[];
    templates: TemplateAdmin[];
    thresholds: Row[];
    config: PublicConfig & Record<string, unknown>;
  }>('adminTrends'),

  listEquipment: () => call<{ ok: true; rows: Row[] }>('adminListEquipment'),
  saveEquipment: (equipment: Row) => call<{ ok: true; created: boolean }>('adminSaveEquipment', { equipment }),
  /** Batches every PMS-day move from the Scheduler board into one round trip. */
  setPmsDays: (updates: Array<{ equipmentId: string; pmsCycleDay: number | null }>) =>
    call<{ ok: true; updated: number; skipped: Array<{ equipmentId: string; reason: string }> }>('adminSetPmsDays', { updates }),

  listTags: (appBaseUrl: string) => call<{ ok: true; rows: Row[]; signingEnabled: boolean }>('adminListTags', { appBaseUrl }),
  saveTag: (tag: Row) => call<{ ok: true; created: boolean }>('adminSaveTag', { tag }),

  listOperators: () => call<{ ok: true; rows: Row[] }>('adminListOperators'),
  saveOperator: (operator: Row) => call<{ ok: true; created: boolean }>('adminSaveOperator', { operator }),

  listTemplates: () => call<{ ok: true; templates: TemplateAdmin[] }>('adminListTemplates'),
  saveTemplate: (template: TemplateAdmin) => call<{ ok: true; templateId: string }>('adminSaveTemplate', { template }),
  deleteTemplate: (templateId: string) => call<{ ok: true }>('adminDeleteTemplate', { templateId }),

  listThresholds: () => call<{ ok: true; rows: Row[] }>('adminListThresholds'),
  saveThreshold: (threshold: Row) => call<{ ok: true }>('adminSaveThreshold', { threshold }),
  deleteThreshold: (threshold: Row) => call<{ ok: true }>('adminDeleteThreshold', { threshold }),

  listReadings: (filter: Row, limit = 200) => call<{ ok: true; rows: Row[] }>('adminListReadings', { filter, limit }),
  approveReading: (submissionId: string, equipmentId: string) =>
    call<{ ok: true; approvedBy: string; approvedAt: string }>('adminApproveReading', { submissionId, equipmentId }),
  dailyLog: (equipmentId: string, date: string) =>
    call<{
      ok: true;
      equipment: Row | null;
      template: { sections: Array<{ key: string; label: string; fields: Array<{ key: string; label: string; unit: string }> }> };
      date: string;
      org?: { name: string; subtitle: string; letterheadUrl: string };
      rows: Row[];
    }>('adminDailyLog', { equipmentId, date }),
  auditLog: (limit = 200) => call<{ ok: true; rows: Row[] }>('adminAuditLog', { limit }),

  getConfig: () => call<{ ok: true; rows: Row[]; effective: PublicConfig & Record<string, unknown> }>('adminGetConfig'),
  saveConfig: (updates: Record<string, unknown>) => call<{ ok: true; effective: Record<string, unknown> }>('adminSaveConfig', { updates }),
};
